<template>
  <section class="hp-model">
    <!-- 背景图 -->
    <div class="hp-model__bg">
      <img :src="bgImage" alt="" />
    </div>

    <div class="hp-model__container">
      <!-- 标题部分 -->
      <div class="hp-model__head" data-aos="fade-up">
        <h2 class="hp-model__title">千橙云AI智能大模型</h2>
        <p class="hp-model__subtitle">
          不是通用大模型，而是
          <span class="hp-model__subtitle-em">"懂零售"</span>
          的垂直大模型
        </p>
      </div>

      <!-- VS 对比模块 -->
      <div class="hp-model__vs" data-aos="fade-up">
        <div class="hp-model__vs-row">
          <!-- 千橙云AI（左侧，60%） -->
          <div class="hp-model__card is-primary">
            <!-- 顶部蓝色条 -->
            <div class="hp-model__card-bar is-primary">
              <!-- 背景装饰 -->
              <div class="hp-model__card-deco">
                <div class="hp-model__card-deco-1"></div>
                <div class="hp-model__card-deco-2"></div>
              </div>
              <div class="hp-model__badge">
                <LucideIcon name="award" :size="14" />
                <span>推荐</span>
              </div>
              <h3 class="hp-model__card-name">千橙云 AI</h3>
              <LucideIcon name="sparkles" :size="16" class="hp-model__card-spark" />
            </div>

            <div class="hp-model__card-body">
              <!-- 输入 -->
              <div class="hp-model__row">
                <span class="hp-model__label is-primary">输入</span>
                <span class="hp-model__value is-lg">"防晒霜促销"</span>
              </div>

              <!-- 输出 -->
              <div class="hp-model__row">
                <span class="hp-model__label is-primary">输出</span>
                <span class="hp-model__value is-lg is-strong">
                  "SPF50+第2件半价，库存2000件，预测点击率6.2%"
                </span>
              </div>

              <!-- 特点 -->
              <div class="hp-model__feature">
                <div class="hp-model__feature-dot">
                  <LucideIcon name="check" :size="12" :stroke-width="2.5" />
                </div>
                <span class="hp-model__value is-lg is-strong">自带库存联动+效果预测</span>
              </div>

              <!-- 数据 -->
              <div class="hp-model__stats is-primary">
                <div class="hp-model__stat">
                  <span class="hp-model__stat-label">零售准确率</span>
                  <span class="hp-model__stat-value is-primary">&gt;87%</span>
                </div>
                <div class="hp-model__stat">
                  <span class="hp-model__stat-label">单次调用成本</span>
                  <span class="hp-model__stat-value is-primary">¥0.001-0.005</span>
                </div>
              </div>
            </div>
          </div>

          <!-- VS 圆形徽章 - 在两个模块之间 -->
          <div class="hp-model__vs-badge">
            <div class="hp-model__vs-badge-inner"><span>VS</span></div>
          </div>

          <!-- 通用大模型（右侧，40%） -->
          <div class="hp-model__card is-plain">
            <!-- 顶部灰色条 -->
            <div class="hp-model__card-bar is-plain">
              <h3 class="hp-model__card-name is-plain">通用大模型</h3>
            </div>

            <div class="hp-model__card-body">
              <!-- 输入 -->
              <div class="hp-model__row">
                <span class="hp-model__label is-plain">输入</span>
                <span class="hp-model__value is-muted">"防晒霜促销"</span>
              </div>

              <!-- 输出 -->
              <div class="hp-model__row">
                <span class="hp-model__label is-plain">输出</span>
                <span class="hp-model__value is-italic">"夏日防晒，守护你的美"</span>
              </div>

              <!-- 特点 -->
              <div class="hp-model__feature">
                <LucideIcon name="x" :size="16" class="hp-model__feature-x" />
                <span class="hp-model__value is-gray">千篇一律，需人工修改</span>
              </div>

              <!-- 数据 -->
              <div class="hp-model__stats is-plain">
                <div class="hp-model__stat">
                  <span class="hp-model__stat-label">零售准确率</span>
                  <span class="hp-model__stat-value is-plain">&lt;40%</span>
                </div>
                <div class="hp-model__stat">
                  <span class="hp-model__stat-label">单次调用成本</span>
                  <span class="hp-model__stat-value is-plain">¥0.1-0.5</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部 4 个数据卡片 -->
      <div class="hp-model__grid" data-aos="fade-up">
        <div v-for="(stat, index) in stats" :key="index" class="hp-model__grid-card">
          <div class="hp-model__grid-card-inner">
            <div class="hp-model__grid-icon">
              <LucideIcon :name="stat.icon" :size="16" :stroke-width="1.5" />
            </div>
            <div class="hp-model__grid-text">
              <div class="hp-model__grid-head">
                <div class="hp-model__grid-value">{{ stat.value }}</div>
                <div class="hp-model__grid-label">{{ stat.label }}</div>
              </div>
              <div class="hp-model__grid-desc">{{ stat.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// 对应 projects-vue 的 src/components/sections/AiModelSection.vue
import LucideIcon from '@/components/LucideIcon.vue'

const STATS = [
  {
    value: '>87%',
    label: '零售场景准确率',
    desc: '通用大模型仅<40%，千橙云AI只学零售，更专业更精准',
    icon: 'target'
  },
  {
    value: '100×',
    label: '成本降低',
    desc: '32B小模型+行业知识内化，单次调用成本低至¥0.001',
    icon: 'dollar-sign'
  },
  {
    value: '25万+',
    label: '门店数据资产',
    desc: '5年+积累，日新增5万条，拥有25万门店的经营日记',
    icon: 'database'
  },
  {
    value: '3端',
    label: '灵活部署',
    desc: '公有云开箱即用、私有化数据隔离、边缘端断网可运行',
    icon: 'server'
  }
]

export default {
  name: 'AiModelSection',
  components: { LucideIcon },
  data() {
    return {
      stats: STATS,
      bgImage: require('@/assets/homePage/ai-model-bg.jpg')
    }
  }
}
</script>

<style lang="less" scoped>
@import './vars.less';

.hp-model {
  position: relative;
  padding: 48px 0;
  overflow: hidden;

  @media (min-width: @screen-md) {
    padding: 64px 0;
  }
}

.hp-model__bg {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
  display: flex;
  justify-content: center;

  img {
    width: 100%;
    max-width: 1600px;
    height: 100%;
    object-fit: contain;
  }
}

.hp-model__container {
  .hp-container();
  position: relative;
}

// ---------- 入场动画 ----------
// ---------- 标题 ----------
.hp-model__head {
  text-align: center;
  margin-bottom: 48px;
}
.hp-model__title {
  margin: 0 0 12px;
  font-size: 36px;
  font-weight: 700;
  color: @hp-ink;
  letter-spacing: -0.025em;
}
.hp-model__subtitle {
  margin: 0 auto;
  max-width: 768px;
  font-size: 16px;
  color: @hp-gray-600;
}
.hp-model__subtitle-em {
  color: @hp-blue;
  font-weight: 600;
}

// ---------- VS 模块 ----------
.hp-model__vs {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;
}
.hp-model__vs-row {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: @screen-md) {
    flex-direction: row;
  }
}

.hp-model__card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  position: relative;

  &.is-primary {
    border: 2px solid @hp-blue;
    box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.1), 0 4px 6px -4px rgba(59, 130, 246, 0.1);

    @media (min-width: @screen-md) {
      width: 60%;
    }
  }

  &.is-plain {
    border: 1px solid @hp-border-200;

    @media (min-width: @screen-md) {
      width: 40%;
    }
  }
}

// 顶部条
.hp-model__card-bar {
  position: relative;
  padding: 16px 24px;
  display: flex;
  align-items: center;

  &.is-primary {
    background: linear-gradient(to right, @hp-blue-dark, @hp-blue);
  }
  &.is-plain {
    background: linear-gradient(to right, @hp-gray-400, @hp-gray-500);
    justify-content: flex-end;
  }
}
.hp-model__card-deco {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.1;
}
.hp-model__card-deco-1 {
  position: absolute;
  top: 0;
  right: 0;
  width: 128px;
  height: 128px;
  background: #fff;
  border-radius: 9999px;
  transform: translate(50%, -50%);
}
.hp-model__card-deco-2 {
  position: absolute;
  bottom: 0;
  left: 80px;
  width: 80px;
  height: 80px;
  background: #fff;
  border-radius: 9999px;
  transform: translateY(50%);
}
.hp-model__badge {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 12px;
  padding: 4px 12px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  color: #fff;

  span {
    font-size: 14px;
    font-weight: 500;
  }
}
.hp-model__card-name {
  position: relative;
  margin: 0;
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.025em;

  @media (min-width: @screen-md) {
    font-size: 22px;
  }

  &.is-plain {
    letter-spacing: normal;
    font-size: 18px;

    @media (min-width: @screen-md) {
      font-size: 20px;
    }
  }
}
.hp-model__card-spark {
  position: relative;
  margin-left: auto;
  color: rgba(255, 255, 255, 0.6);
}

// 卡片内容
.hp-model__card-body {
  padding: 24px;

  > * + * {
    margin-top: 16px;
  }
}
.hp-model__row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.hp-model__label {
  flex-shrink: 0;
  font-size: 14px;

  &.is-primary {
    color: @hp-blue;
    font-weight: 500;
    background: rgba(45, 111, 247, 0.08);
    padding: 2px 8px;
    border-radius: 4px;
  }
  &.is-plain {
    color: @hp-gray-500;
  }
}
.hp-model__value {
  font-size: 16px;
  color: @hp-ink;

  // 仅左侧千橙云卡片的文案在 md 以上放大到 18px
  &.is-lg {
    @media (min-width: @screen-md) {
      font-size: 18px;
    }
  }
  &.is-strong {
    font-weight: 500;
  }
  &.is-muted {
    color: @hp-gray-700;
  }
  &.is-gray {
    color: @hp-gray-500;
  }
  &.is-italic {
    color: @hp-gray-500;
    font-style: italic;
  }
}
.hp-model__feature {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hp-model__feature-dot {
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: rgba(45, 111, 247, 0.1);
  color: @hp-blue;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hp-model__feature-x {
  color: @hp-gray-400;
  flex-shrink: 0;
}
.hp-model__stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;

  &.is-primary {
    border-top: 1px solid @hp-border;
  }
  &.is-plain {
    border-top: 1px solid @hp-border-200;
  }

  @media (min-width: @screen-sm) {
    flex-direction: row;
    align-items: baseline;
    gap: 24px;
  }
}
.hp-model__stat {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.hp-model__stat-label {
  font-size: 14px;
  color: @hp-gray-500;
}
.hp-model__stat-value {
  font-size: 20px;
  font-weight: 700;

  &.is-primary {
    color: @hp-blue;

    @media (min-width: @screen-md) {
      font-size: 22px;
    }
  }
  &.is-plain {
    color: @hp-gray-400;
  }
}

// VS 徽章
.hp-model__vs-badge {
  display: none;

  @media (min-width: @screen-md) {
    display: flex;
    position: absolute;
    left: 60%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
  }
}
.hp-model__vs-badge-inner {
  width: 64px;
  height: 64px;
  background: @hp-blue;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid #fff;
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -4px rgba(59, 130, 246, 0.3);

  span {
    color: #fff;
    font-size: 20px;
    font-weight: 700;
  }
}

// ---------- 底部 4 卡 ----------
.hp-model__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  text-align: left;

  @media (min-width: @screen-md) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: @screen-lg) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.hp-model__grid-card {
  background: #fff;
  border: 1px solid @hp-border;
  border-radius: 16px;
  padding: 16px;
}
.hp-model__grid-card-inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.hp-model__grid-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(45, 111, 247, 0.1);
  color: @hp-blue;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hp-model__grid-text {
  flex: 1 1 0%;
  min-width: 0;
}
.hp-model__grid-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 4px;
}
.hp-model__grid-value {
  font-size: 20px;
  font-weight: 700;
  color: @hp-ink;
}
.hp-model__grid-label {
  font-size: 14px;
  font-weight: 500;
  color: @hp-gray-500;
}
.hp-model__grid-desc {
  font-size: 14px;
  line-height: 1.625;
  color: @hp-gray-600;
}
</style>
